import type { MessageTree } from '../types';

/** French UI copy — catalog payloads stay in their original language. */
export const fr: MessageTree = {
  brand: {
    tagline: 'Payment Explorer',
  },
  ad: {
    label: 'Publicité',
  },
  consent: {
    title: 'Cookies et publicités',
    body: 'Nous utilisons Google AdSense sur les pages du catalogue. Acceptez les annonces personnalisées, ou continuez avec des annonces non personnalisées uniquement. Détails dans',
    privacy: 'Confidentialité',
    accept: 'Accepter les pubs personnalisées',
    reject: 'Non personnalisées seulement',
  },
  footer: {
    version: 'v{{version}}',
    build: '#{{build}}',
    copyright: '© {{year}} GeoKing',
    license: 'GPL-3.0',
  },
  nav: {
    menu: 'Menu',
    close: 'Fermer',
    search: 'Rechercher paiements, messages, schémas…',
    start: 'Paiements',
    overview: 'Accueil',
    try: 'Éditeur d’essai',
    live: 'Démo live',
    map: 'Carte d’interop',
    glossary: 'Glossaire',
    about: 'À propos',
    privacy: 'Confidentialité',
    contact: 'Contact',
    standards: 'Standards API',
    flows: 'Flux',
    messages: 'ISO 20022',
    schemes: 'Schémas',
    infrastructure: 'Infrastructures',
    tools: 'Outils',
    catalog: 'Catalogue technique',
    quiz: 'Quiz debug',
    compareVersions: 'Comparer pacs.008',
    darkMode: 'Mode sombre',
    lightMode: 'Mode clair',
  },
  locale: {
    label: 'Langue',
    en: 'EN',
    fr: 'FR',
  },
  category: {
    'account-information': 'Information sur le compte',
    'payment-initiation': 'Initiation de paiement',
    'funds-confirmation': 'Confirmation de fonds',
    clearing: 'Compensation & règlement',
    exception: 'Gestion des exceptions',
    scheme: 'Schémas de paiement',
  },
  area: {
    pain: 'Initiation des paiements',
    pacs: 'Compensation & règlement',
    camt: 'Gestion de trésorerie',
    acmt: 'Gestion de compte',
    auth: 'Autorités',
    remt: 'Avis de remittance',
  },
  family: {
    'iso-tx-status': 'Statut de transaction ISO 20022',
    'iso-status-reason': 'Motif de statut ISO 20022',
    'bg-error': 'Erreur Berlin Group',
    'stet-error': 'Erreur STET',
    'ukob-error': 'Erreur UK Open Banking',
    'sca-status': 'Statut SCA',
    'consent-status': 'Statut de consentement',
    'scheme-status': 'Statut schéma (overlay A2A)',
  },
  search: {
    title: 'Recherche',
    placeholder: 'DbtrAgt, pacs.008, AC01, consentement, recall…',
    emptyShort: 'Saisissez au moins deux caractères.',
    emptyNone:
      'Aucun résultat. Essayez une balise ISO (DbtrAgt), un code, un identifiant de message ou un standard.',
    clear: 'Effacer la recherche',
    move: '↑↓ naviguer',
    open: '↵ ouvrir',
    results: '{{count}} résultats',
  },
  kind: {
    standard: 'Standard',
    message: 'Message',
    flow: 'Flux',
    code: 'Code',
    sample: 'Exemple',
    endpoint: 'Endpoint',
    term: 'Terme',
    payment: 'Paiement',
    scheme: 'Schéma',
    infrastructure: 'Rail',
    live: 'Démo',
  },
  home: {
    eyebrow: 'Paiements européens',
    title1: 'Comprendre comment fonctionnent les paiements.',
    title2: '',
    lead:
      'Commencez par la différence entre paiements classiques et instantanés face au CSM au milieu, puis explorez flux, messages ISO 20022, schémas et rails.',
    noticeTitle: 'À propos de ce portail',
    noticePurpose:
      'Ce portail rassemble des informations sur les processus financiers pour expliquer standards, acteurs, termes, flux et concepts associés.',
    noticeAi: 'Propulsé par l’IA — des erreurs peuvent être présentes.',
    noticeNoMoney: 'Aucun argent réel n’est en jeu ici.',
    noticePublic: 'Toutes les données présentées sont publiques.',
    noticeInfoOnly: 'Il s’agit uniquement d’un site informatif.',
    searchPrompt: 'Que voulez-vous explorer ?',
    explorePayment: 'Explorer un paiement',
    thenTrace: 'Commencez par le récit, puis ouvrez la trace technique hop par hop.',
    popular: 'Populaire',
    seeAlso: 'Voir aussi',
    storyEyebrow: 'Suivre 100 €',
    storyHeadline: 'Comment 100 € voyagent-ils de la France vers l’Allemagne ?',
    ctaTry: 'Éditeur — pacs.008 → pacs.002',
    ctaMap: 'Ouvrir la carte d’interop',
    ctaMapHint: 'standards × rails',
    ctaSct: 'SCT Inst — chemin heureux',
    ctaAck: 'pacs.002 ack XML',
    traceFlow: 'Suivre un flux',
    steps: '{{count}} étapes',
    standards: 'Standards couverts',
    instant: 'Paiements instantanés (SCT Inst / IPR)',
    instantHappy: 'Chemin heureux ≤10s',
    instantVop: 'VoP puis instantané (IPR)',
    instantReject: 'Rejet dans la fenêtre',
    instantTimeout: 'Timeout & pacs.028',
    instantRecall: 'Rappel après règlement',
    instantSic: 'SIC IP (CHF)',
    sctStandard: 'Standard SCT Inst & pièges →',
    inspectXml: 'Inspecter le XML ISO 20022',
    blurb008: 'Virement client FI-to-FI — le paiement interbancaire lui-même.',
    blurb002: 'Statut / accusé de réception — ACSC, RJCT et motifs.',
    blurb001: 'Initiation de virement client vers la banque.',
    blurb056: 'Demande d’annulation / rappel après règlement.',
    traceClearing: 'Suivre pacs.008 → pacs.002 ack en compensation →',
    readId: 'Lire un identifiant',
    idHint:
      'La variante et la version sont deux choses différentes. Une banque en variante 001 version 09 rejettera votre payload version 08, et l’erreur le dira rarement.',
    open008: 'Ouvrir pacs.008 →',
    lookCode: 'Chercher un code',
    allCodes: 'Tous les {{count}} codes →',
    footer:
      '{{payments}} paiements · {{messages}} messages · {{codes}} codes. Appuyez sur ⌘K pour rechercher.',
    apiLayer: 'COUCHE API — CE QUE VOIT le TPP',
    clearingLayer: 'COUCHE COMPENSATION — CE QUE VOIT LA BANQUE',
    reasonStop: 'les motifs s’arrêtent ici',
    samePayment:
      'Le même paiement. Un TPP voit « rejected » ; la banque voit AC01 et sait que l’IBAN est faux.',
    overview: {
      eyebrow: 'Comment le système s’articule',
      title: 'Classique, instantané, et le CSM au milieu',
      lead:
        'Un schéma dit ce qui doit se passer. Un CSM est l’endroit où les banques compensent et règlent. Même forme de pacs.008 — horloge, cut-off et rail différents.',
      schemeTitle: 'Schéma (règles métier)',
      schemeBody:
        'SCT, SCT Inst, SDD : qui doit être joignable, quels messages ISO, quel SLA. Le rulebook — pas le fil.',
      csmTitle: 'CSM (le rail)',
      csmBody:
        'Mécanisme de compensation et de règlement : STEP2 pour le SEPA par lots, TIPS ou RT1 pour l’instantané. Les banques y échangent les pacs.',
      vsTitle: 'Classique vs instantané',
      vsBody:
        'Le SCT classique attend le prochain lot / jour TARGET. L’instantané (défaut IPR pour les CT euro) se règle en ≤10 secondes, 24/7, avec LclInstrm=INST.',
      linkRegular: 'Explorer le SCT',
      linkInstant: 'Explorer le SCT Inst',
      diagramEyebrow: 'Acteurs & parcours de l’argent',
      diagramHint: 'Changez le type de paiement — le CSM et qui démarre l’instruction changent.',
      modeLabel: 'Type de paiement',
      mode: {
        regular: 'SCT classique',
        instant: 'SCT Inst',
        sdd: 'Prélèvement',
      },
      modeLead: {
        regular:
          'Le payeur initie un virement. La banque débitrice envoie des pacs.008 par lots dans STEP2 (ou équivalent). Le créancier voit les fonds au prochain cycle de règlement — pas en secondes.',
        instant:
          'Mêmes acteurs et rôles que le SCT classique, mais le CSM est TIPS ou RT1. Fonds disponibles en ≤10 secondes ; rejet ou timeout doivent tenir dans cette fenêtre.',
        sdd:
          'Le créancier initie le prélèvement (pain.008). L’argent quitte toujours le Débiteur et arrive au Créancier — le côté qui démarre s’inverse, pas les côtés ISO.',
      },
      partyPayer: 'Payeur',
      partyBene: 'Bénéficiaire',
      partyCreditor: 'Créancier',
      bankDebtor: 'Banque débitrice',
      bankCreditor: 'Banque créancière',
      csmNode: 'CSM',
      csmBatch: 'STEP2 / lots',
      csmInstant: 'TIPS / RT1',
      slaRegular: 'Prochain jour TARGET',
      slaInstant: '≤10 secondes · 24/7',
      msgsRegular: 'pain.001 → pacs.008 → pacs.002',
      msgsInstant: 'pain.001 → pacs.008 INST → pacs.002 ACSC',
      msgsSdd: 'pain.008 → pacs.003 → pacs.002',
      rolesEyebrow: 'Vocabulaire',
      rolesTitle: 'Débiteur / Créancier vs Orig / Bene',
      rolesLead:
        'ISO 20022 nomme les côtés de compte (qui est débité / crédité). Les ops disent souvent originator / beneficiary pour qui démarre ou reçoit l’instruction.',
      roleDebtor: 'Débiteur',
      roleCreditor: 'Créancier',
      roleOriginator: 'Originator',
      roleBeneficiary: 'Beneficiary',
      rolePayer: 'Payeur',
      roleDebtorPlain: 'Compte qui perd l’argent (Dbtr / DbtrAcct). Toujours le côté payeur économiquement.',
      roleCreditorPlain: 'Compte qui gagne l’argent (Cdtr / CdtrAcct). Toujours le côté bénéficiaire économiquement.',
      roleOriginatorPlain: 'Partie qui démarre l’instruction — payeur sur un virement, créancier sur un prélèvement.',
      roleBeneficiaryPlain: 'Partie qui reçoit le crédit sur un virement (souvent = Créancier).',
      mapEyebrow: 'Carte rapide',
      mapTitle: 'Mêmes mots, paiements différents',
      mapLead:
        'Sur SCT et SCT Inst, Originator ≈ Débiteur et Beneficiary ≈ Créancier. Sur SDD, le Créancier est l’Originator du prélèvement.',
      colPayment: 'Paiement',
      colDebit: 'Débité',
      colCredit: 'Crédité',
      colStarts: 'Qui démarre',
      mapSctDebit: 'Payeur = Débiteur = Orig',
      mapSctCredit: 'Bene = Créancier',
      mapSctStarts: 'Payeur / banque débitrice',
      mapSddDebit: 'Payeur = Débiteur',
      mapSddCredit: 'Créancier = Orig',
      mapSddStarts: 'Créancier / banque créancière',
      mapFoot:
        'Dans le XML cherchez Dbtr / Cdtr, pas « payeur ». Les agents sont DbtrAgt / CdtrAgt. La compensation reste dans le CSM au milieu.',
    },
  },
  explorer: {
    eyebrow: 'Payment Explorer',
    scheme: 'Schéma',
    infrastructure: 'Infrastructure',
    viaLabel: 'Qui démarre',
    viaHint: 'Le payeur chez sa banque, ou une appli de paiement qu’il a autorisée (un PISP).',
    viaBank: 'Banque',
    viaPisp: 'Appli (PISP)',
    viaWero: 'Overlay A2A',
    viaMerchant: 'Commerçant',
    viaCreditor: 'Créancier',
    country: 'Pays du payeur',
    countryHint: 'Où se trouve le compte du payeur. Les horaires de cut-off et le réseau habituel peuvent changer.',
    cutoff: 'Cut-off',
    reachability: 'Joignabilité',
    localException: 'Notes locales',
    countryNotesLead: 'Ce que ce pays change pour la banque du payeur.',
    rail: 'Quel réseau',
    railHint: 'Le système entre banques qui déplace vraiment l’argent — pas l’appli que l’on touche.',
    outcome: 'Comment ça se termine',
    outcomeHint: 'L’argent arrive, l’autre banque refuse, personne ne répond à temps, ou une banque demande plus tard d’annuler un crédit (rappel).',
    happy: 'Arrivé',
    reject: 'Refusé',
    timeout: 'Pas de réponse',
    recall: 'Rappel',
    compare: 'Comparer avec',
    compareHint: 'Les mêmes 100 €, autre schéma — voyez quels hops diffèrent.',
    compareVs: 'SCT',
    howTo:
      'Lisez d’abord le récit numéroté. Les interrupteurs changent la situation. Quand une étape est claire, ouvrez une trace technique pour voir les messages échangés par les banques.',
    tracesHint: 'Le même parcours en messages ISO 20022. Ouvrez-en un pour la séquence et le XML.',
    questionsTitle: 'Quatre questions',
    qWho: 'Qui agit ?',
    qSystem: 'Quel système ?',
    qMessages: 'Quels messages ?',
    qStandards: 'Quelles règles ?',
    noMessage: 'Pas de message ISO sur ce hop',
    thisHop: 'Ce hop',
    openTrace: 'Voir ce hop comme un échange de messages →',
    layerTraces: 'Aller plus loin',
    tryInLive: 'Essayer en démo grandeur nature',
    messagePage: 'Fiche message',
    inspectSample: 'Inspecter l’exemple',
    related: 'Lié',
    usedBy: 'Utilisé par',
    sources: 'Sources',
    exploreThis: 'Explorer ce paiement',
    vocabLabel: 'Libellés des acteurs',
    vocabHint:
      'Noms courants (Payeur / Bénéficiaire), raccourcis ops (Orig / Bene), ou côtés de compte ISO (Dbtr / Cdtr). Sur SDD le créancier est Orig.',
    vocab: {
      plain: 'Courant',
      ops: 'Orig / Bene',
      iso: 'Dbtr / Cdtr',
    },
    actor: {
      payer: 'Payeur',
      bankA: 'Banque du payeur',
      csm: 'Compensation / règlement',
      bankB: 'Banque du bénéficiaire',
      beneficiary: 'Bénéficiaire',
      scheme: 'Schéma',
      merchant: 'Commerçant',
      acquirer: 'Acquéreur',
    },
  },
  compare: {
    eyebrow: 'Comparaison de versions',
    title: 'Révisions de schéma',
    lead:
      'Même message métier, xmlns différent. Les marchés choisissent une révision via les usage guidelines — le SEPA reste souvent en .08 tandis que CBPR+ passe en .10 / .13.',
    from: 'De',
    to: 'Vers',
    diff: 'Ce qui change',
    sameVersion: 'Même révision sélectionnée des deux côtés.',
    xmlnsDiff: 'Le namespace Document passe de {{from}} à {{to}}.',
    schemaDiff: 'Nom de type XSD : {{from}} → {{to}}.',
    marketsAdded: 'Marchés mis en avant à droite : {{list}}.',
    marketsRemoved: 'Marchés mis en avant à gauche seulement : {{list}}.',
    statusDiff: 'Statut catalogue : {{from}} → {{to}}.',
    backMessage: 'Ouvrir la fiche message →',
  },
  quiz: {
    eyebrow: 'Debugger ce paiement',
    title: 'Quiz de débogage paiement',
    lead:
      'Entraînez-vous au diagnostic sur les messages de compensation ISO 20022 et les réponses API Open Banking. Lisez le payload et choisissez le code motif ou code d’erreur exact.',
    progress: 'Question {{current}} sur {{total}}',
    score: 'Score : {{score}} / {{total}}',
    prompt: 'Sélectionnez le code présent dans le payload',
    correct: 'Correct !',
    incorrect: 'Incorrect.',
    prev: '← Précédent',
    next: 'Question suivante →',
    restart: 'Recommencer le quiz',
    completedTitle: 'Quiz terminé !',
    completedLead: 'Votre score est de {{score}} sur {{total}} ({{percent}} %).',
    openExplorer: 'Explorer le flux lié →',
    payload: 'Exemple de payload inspecté',
    openEditor: 'Ouvrir dans l’éditeur d’essai →',
  },
  map: {
    eyebrow: 'Carte d’interop',
    title: 'Standards, messages et rails',
    lead:
      'Un réseau Cytoscape de la façon dont les standards XS2A / Open Banking se relient aux messages ISO 20022 et aux rails de compensation (SEPA, SIC, euroSIC, SIC IP, Wero). Utilisez une page de flux pour les swimlanes ordonnées ; utilisez cette carte pour la topologie.',
  },
  glossary: {
    eyebrow: 'Glossaire',
    title: 'Glossaire des paiements',
    lead:
      'Définitions courtes des termes qui reviennent en PSD2, SCT Inst, Open Banking et ISO 20022 — y compris chaque statut et motif, et si un nouvel essai est légitime. Basculez la langue pour les intitulés français.',
    sourcesLead:
      'Aligné sur des glossaires publics (Open Banking UK, Mastercard Open Finance US, Konsentus Europe, PSD2 Bundesbank, sigles PSD Ravelin). Citations complètes dans',
    placeholder: 'SCT, TIPS, PIIS, VoP, AC01…',
    filterAria: 'Filtrer le glossaire',
    all: 'Tous {{count}}',
    empty: 'Aucun résultat. Essayez SCT, TIPS, VoP, AC01, ou un fragment comme « consent ».',
    alsoCalled: 'Aussi appelé',
    seeAlso: 'Voir aussi',
    relatedFlows: 'Flux associés',
    explore: 'Explorer dans le catalogue',
    alignedWith: 'Aligné sur',
    action: 'Que faire',
  },
  about: {
    eyebrow: 'À propos',
    title: 'À propos de cet explorateur',
    lead:
      'Un paiement traverse une frontière à mi-parcours : JSON sur HTTPS côté TPP, XML ISO 20022 côté banque. Ce site réunit les deux vocabulaires dans un seul catalogue.',
    glossaryTitle: 'Sources du glossaire',
    glossaryLead:
      'Les termes, codes et sigles du glossaire sont écrits pour les intégrateurs. Quand une entrée correspond à un glossaire public, la source est liée sur l’entrée et listée ici.',
    glossaryCount: '{{count}} entrées dans le catalogue.',
    glossaryNote:
      'Les entrées Mastercard décrivent le vocabulaire produit Open Finance US (Data Connect, FinBanks, Partner Linked). Elles côtoient les termes PSD2 UE/UK pour couvrir les deux dialectes — ce ne sont pas des définitions juridiques interchangeables.',
    publisher:
      'OpenFinance est publié par GeoKing. C’est un catalogue pédagogique des schémas de paiement européens, des infrastructures et des messages ISO 20022. Ce n’est ni une banque, ni un service de paiement, ni un conseil financier, juridique ou en investissement.',
  },
  privacy: {
    eyebrow: 'Mentions',
    title: 'Politique de confidentialité',
    updated: 'Dernière mise à jour : 17 août 2026.',
    who: 'Ce site (openfinance.geoking.fr) est exploité par GeoKing. Il explique le fonctionnement des flux de paiement européens, des schémas SEPA et des messages ISO 20022. Nous ne créons pas de comptes utilisateur et nous ne vous demandons pas d’identifiants de paiement.',
    dataTitle: 'Ce que nous conservons',
    data: 'Dans votre navigateur, nous pouvons enregistrer une préférence de langue, un thème clair/sombre, et votre choix de personnalisation AdSense (accepter ou non personnalisées seulement) en stockage local. Le site n’embarque pas d’outil d’analytics first-party. Les journaux serveur chez Cloudflare peuvent contenir une adresse IP tronquée, un user-agent et l’URL demandée, uniquement pour faire fonctionner et sécuriser le site.',
    adsTitle: 'Publicité',
    ads: 'Nous utilisons Google AdSense pour afficher des annonces sur les pages du catalogue qui contiennent des explications éditoriales. Google et ses partenaires publicitaires peuvent utiliser des cookies ou technologies similaires pour diffuser des annonces personnalisées ou non personnalisées, mesurer les performances et lutter contre la fraude. Avant le chargement des annonces personnalisées, un bandeau du site demande votre consentement. Si vous refusez, nous demandons uniquement des annonces non personnalisées. Google Funding Choices peut aussi s’afficher pour l’EEE, le Royaume-Uni ou la Suisse.',
    googlePolicy: 'Règles de confidentialité Google',
    cookiesTitle: 'Cookies',
    cookies: 'AdSense peut déposer des cookies sur notre domaine et sur les domaines Google après votre choix (personnalisées ou non). Ils ne sont pas nécessaires pour lire le catalogue. Les bloquer peut masquer les annonces ; les articles restent accessibles.',
    contactTitle: 'Contact',
    contact: 'Questions sur cette politique ou sur les données personnelles :',
  },
  contact: {
    eyebrow: 'Éditeur',
    title: 'Contact',
    lead: 'Écrivez à l’éditeur pour une question sur OpenFinance, ce catalogue, ou l’usage d’AdSense sur le site.',
    emailLabel: 'E-mail :',
    publisher: 'GeoKing publie OpenFinance comme site pédagogique. Nous ne pouvons pas traiter de paiements, ouvrir de comptes, ni répondre sur un virement bancaire particulier.',
  },
  notFound: {
    title: 'Rien à ce chemin',
    body: 'Le standard, flux ou message demandé n’est pas dans le catalogue. Cherchez avec ⌘K pour trouver ce qui l’est.',
    back: 'Retour à la vue d’ensemble',
  },
  flow: {
    txFlow: 'Flux de transaction',
    sequence: 'Séquence',
    diagramType: 'Type de diagramme',
    entities: 'Entités',
    participation: 'Adhésion clearing',
    directParticipant: 'Direct',
    subParticipant: 'Sous-participant',
    directParticipantHint:
      'Membre compensateur direct : les contrôles de liquidité ILM restent sur le chemin avant règlement.',
    subParticipantHint:
      'Sous-participant pré-financé : les jambes ILM sont omises — le hub enchaîne directement vers le core banking / règlement.',
    clickStep: 'Cliquez une étape, ou tabulez puis Entrée',
    fullscreen: 'Plein écran',
    exitFullscreen: 'Quitter le plein écran',
    apiHop: 'Saut API',
    clearingHop: 'Saut compensation',
    clickArrow: 'Cliquez une flèche pour inspecter le saut',
    clickMessage: 'Cliquez l’étiquette sur une flèche (pacs.008, …) pour l’info, l’usage et l’exemple',
    runScenario: 'Lancer le scénario',
    pause: 'Pause',
    restart: 'Recommencer',
    playback: 'Lecture',
    messages: 'Messages',
    technicalViewOf: 'Vue technique de',
    messagePage: 'Fiche complète →',
    usageHere: 'Dans ce flux',
    usageOther: 'Aussi utilisé dans',
    noPayload: 'Pas de payload',
    noPayloadBody:
      'Cette étape est une redirection, une attente ou un événement hors bande — aucun message sur le fil à inspecter. Les étapes avec payload sont marquées d’une méthode ou d’un id de message.',
    openSample: 'Ouvrir la page exemple →',
    allSamples: 'Tous les exemples {{short}} →',
    openTry: 'Éditeur d’essai — ouvrir les exemples →',
    useCase: 'Pourquoi lire ceci',
    tryInLive: 'Essayer en démo grandeur nature',
    seeStepInLive: 'Voir cette étape en démo →',
    samples: 'Exemples de ce flux',
    prev: '← Précédent',
    next: 'Étape suivante →',
    headers: 'En-têtes importants',
    codesHere: 'Codes visibles ici',
    message: 'Message',
  },
  message: {
    facts: 'Faits',
    root: 'Élément racine',
    direction: 'Direction',
    namespace: 'Espace de noms',
    variant: 'Variante (flavour)',
    version: 'Version',
    country: 'Pays',
    guideline: 'Guide d’usage',
    versions: 'Versions de schéma',
    versionsLead:
      'SWIFT et l’ISO publient des révisions XSD successives du même message métier (ex. pacs.008.001.08 → .10 → .13 ; même logique pour camt et pain). Les marchés en figent une via un guide d’usage (EPC SEPA, CBPR+, SPS suisse…) et peuvent ajouter un suffixe pays (pacs.008.001.08.ch.02) — le xmlns Document doit correspondre à ce XSD.',
    versionsEditLead:
      'Choisissez la révision de schéma, la variante (flavour) ou le guide d’usage national ciblé. L’éditeur met à jour le xmlns Document (et la réponse appariée) en conséquence.',
    variantShort: 'var {{variant}}',
    versionsXsd: 'Catalogue ISO (XSD)',
    versionsSwift: 'SWIFT / CBPR+',
    versionsGlossary: 'Glossaire SWIFT & versionnement',
    markets: 'Marchés / rails',
    versionCurrent: 'courante',
    versionLegacy: 'héritée',
    versionUpcoming: 'à venir',
    sampleVersionMismatch:
      'Les exemples bundlés utilisent encore un autre xmlns ; aucun ne correspond à {{version}}. Vérifiez le payload avant d’envoyer à une banque sur cette révision.',
    required: 'Éléments qu’un validateur exige',
    requiredHint:
      'Pas le XSD complet — les chemins dont l’absence provoque les rejets que l’on voit vraiment en production.',
    startWithStory: 'Commencer par le récit de paiement',
    appearsIn: 'Puis les flux techniques',
    usage: 'Usage dans les flux',
    openSample: 'Ouvrir la page exemple →',
    editHint: 'Éditez dans l’onglet XML / JSON, puis lancez le contrôle.',
    modified: 'Modifié —',
    reset: 'réinitialiser',
    noSample:
      'Aucun exemple pour {{short}} pour l’instant. Ajoutez-en un dans src/data/samples.ts : il apparaîtra ici, dans la recherche et dans les flux qui le référencent.',
    dirCustomerBank: 'Client → banque',
    dirBankCustomer: 'Banque → client',
    dirBankBank: 'Banque → banque',
    dirBankCsm: 'Banque → compensation',
    ackEyebrow: 'accusé',
    ackNote:
      'Ceci est l’accusé de compensation : TxSts porte ACSC (réglé), RJCT (refusé), etc. Associez-le au pacs.008 d’origine, ou construisez les deux dans l’éditeur d’essai.',
    build008:
      'Construisez un pacs.008 personnalisé et voyez l’accusé pacs.002 correspondant dans l’éditeur d’essai.',
    openEditor: 'Ouvrir dans l’éditeur d’essai →',
    workspaceMode: 'Mode espace de travail',
    modeView: 'Voir',
    modeEdit: 'Éditer',
    helpMode: 'Aide',
    schemaForm: 'Champs schéma',
    schemaUnavailable: 'Pas encore de modèle XSD pour ce message — éditez le XML dans l’inspecteur.',
    schemaNeedXml: 'Fournissez un XML bien formé pour éditer les champs schéma.',
    schemaAdd: 'Ajouter {{name}}',
    schemaRemove: 'Retirer',
    schemaRequiredMissing: 'requis',
    xmlJsonHint: 'Basculez XML | JSON dans l’inspecteur sans quitter cet exemple.',
  },
  try: {
    eyebrow: 'Éditeur d’essai',
    title: 'Construire un pacs.008, obtenir un pacs.002',
    titleGeneric: 'Éditer {{short}}',
    lead:
      'Remplissez les champs. Concentrez-vous sur le pacs.008 à envoyer ; révélez l’accusé (ou le rejet) pacs.002 quand vous en avez besoin. Basculez XML | JSON, éditez, puis validez.',
    leadGeneric:
      'Partez d’un exemple synthétique, éditez le XML librement, puis validez contre les chemins requis du catalogue. Les réponses appariées (ex. pain.002) restent masquées jusqu’à ce que vous les affichiez.',
    showResponse: 'Afficher {{short}}',
    hideResponse: 'Masquer {{short}}',
    messageType: 'Type de message',
    rootElement: 'Élément racine',
    sampleVariant: 'Exemple',
    catalogPage: 'Page catalogue',
    tagBuilder: 'constructeur',
    tagSample: 'exemple',
    titlePrimary: '{{short}} — message à éditer',
    titlePair: '{{short}} — réponse appariée',
    editPrimary: 'Éditez dans l’onglet XML / JSON. Réinitialiser pour restaurer l’exemple.',
    editPair:
      'Éditez l’exemple de réponse appariée. Les identifiants peuvent ne pas correspondre à vos edits primaires sauf alignement manuel.',
    fields: 'Champs de paiement',
    reset: 'Réinitialiser',
    instant: 'SCT Inst (LclInstrm=INST)',
    instantDef:
      'Active l’instrument local INST — chemin SCT Instant / IPR. Affecte PmtTpInf dans le pacs.008.',
    clearing: 'Système de compensation',
    clearingDef: 'Système de règlement dans SttlmInf/ClrSys (TIPS, RT1, STEP2, SIC, euroSIC).',
    outcome: 'Résultat pacs.002',
    outcomeDef: 'Ce que renverrait l’agent créancier : réglé (ACSC) ou rejeté (RJCT + motif).',
    ack: 'Ack ACSC',
    reject: 'RJCT',
    reason: 'Code motif',
    reasonDef: 'Motif de statut ISO externe (ex. AC01 IBAN faux, AM04 fonds insuffisants, AB05 timeout).',
    related: 'Références liées :',
    edit008:
      'Éditez dans l’onglet XML / JSON. Changer le formulaire régénère sauf si vous avez des edits locales — Réinitialiser pour les effacer.',
    ack002:
      'Généré avec les mêmes identifiants que le pacs.008 pour que EndToEndId / TxId / OrgnlMsgId s’alignent.',
    title008: 'pacs.008 — virement que vous construisez',
    title002Ack: 'pacs.002 — accusé ACSC',
    title002Rjct: 'pacs.002 — RJCT ({{code}})',
    sectionMoney: 'Montant',
    sectionIds: 'Identifiants',
    sectionDebtor: 'Débiteur',
    sectionCreditor: 'Créancier',
    sectionRemittance: 'Remittance',
    sectionTiming: 'Horodatage',
    sectionClearing: 'Compensation',
    fieldAmount: 'Montant',
    fieldCurrency: 'Devise',
    fieldEndToEndId: 'EndToEndId',
    fieldInstructionId: 'InstrId',
    fieldTxId: 'TxId',
    fieldMsgId: 'MsgId',
    fieldDebtorName: 'Nom du débiteur',
    fieldDebtorIban: 'IBAN débiteur',
    fieldDebtorBic: 'BIC débiteur',
    fieldCreditorName: 'Nom du créancier',
    fieldCreditorIban: 'IBAN créancier',
    fieldCreditorBic: 'BIC créancier',
    fieldRemittance: 'Remittance',
    fieldSettlementDate: 'Date de règlement',
    fieldCreatedAt: 'Créé le',
    defAmount: 'IntrBkSttlmAmt — montant de règlement interbancaire. Décimal avec un point (42.50).',
    defCurrency: 'Devise ISO 4217 sur IntrBkSttlmAmt (EUR, CHF, …).',
    defEndToEndId: 'Référence de bout en bout conservée entre banques (PmtId/EndToEndId). Max 35 car.',
    defInstructionId: 'Identifiant d’instruction de la partie initiatrice (PmtId/InstrId).',
    defTxId: 'Id de transaction unique du premier agent instructeur (PmtId/TxId).',
    defMsgId: 'Identifiant du pacs.008 (GrpHdr/MsgId). Repris en OrgnlMsgId dans le pacs.002.',
    defDebtorName: 'Dbtr/Nm — nom de la partie dont le compte est débité.',
    defDebtorIban: 'IBAN DbtrAcct — compte débiteur. La plage de test FR76… convient ici.',
    defDebtorBic: 'BICFI DbtrAgt — agent débiteur (banque émettrice).',
    defCreditorName: 'Cdtr/Nm — partie dont le compte est crédité.',
    defCreditorIban: 'IBAN CdtrAcct — compte créancier.',
    defCreditorBic: 'BICFI CdtrAgt — agent créancier (banque réceptrice).',
    defRemittance: 'RmtInf/Ustrd — remittance non structurée visible du bénéficiaire.',
    defSettlementDate: 'IntrBkSttlmDt — date calendaire du règlement interbancaire (AAAA-MM-JJ).',
    defCreatedAt: 'CreDtTm / AccptncDtTm — création du message (ISO-8601 UTC).',
    datePresets: 'Dates rapides',
    dateYesterday: 'Hier',
    dateToday: 'Aujourd’hui',
    dateTomorrow: 'Demain',
    dateNow: 'Maintenant',
    dateMinus1h: '−1h',
    datePlus1h: '+1h',
    dateMinus1d: '−1j',
    datePlus1d: '+1j',
    dateStartToday: '00:00 UTC',
    dateNoonToday: '12:00 UTC',
    paneMode: 'Panneaux XML',
    paneBoth: 'Les deux',
    modeSimple: 'Simple',
    modeExpert: 'Expert',
    expertHint:
      'Chaque feuille du XML généré. Cliquez un tag dans l’arbre pour focuser le champ ; focuser un champ met le tag en évidence.',
    sectionDocument: 'Document',
    sectionGrpHdr: 'GrpHdr',
    sectionPmtId: 'PmtId',
    sectionPmtTpInf: 'PmtTpInf',
    sectionTxInf: 'CdtTrfTxInf',
    sectionStatus: 'pacs.002',
    fieldXmlns: 'xmlns',
    fieldNbOfTxs: 'NbOfTxs',
    fieldTtlAmount: 'TtlIntrBkSttlmAmt',
    fieldTtlCurrency: 'Ttl Ccy',
    fieldGrpSettlementDate: 'GrpHdr IntrBkSttlmDt',
    fieldSttlmMtd: 'SttlmMtd',
    fieldInstgAgt: 'InstgAgt BICFI',
    fieldInstdAgt: 'InstdAgt BICFI',
    fieldSvcLvl: 'SvcLvl',
    fieldLclInstrm: 'LclInstrm',
    fieldChrgBr: 'ChrgBr',
    fieldAccptncDtTm: 'AccptncDtTm',
    fieldStatusMsgId: 'pacs.002 MsgId',
    fieldOrgnlMsgNmId: 'OrgnlMsgNmId',
    fieldAddtlInf: 'AddtlInf',
    defXmlns: 'Namespace Document — doit correspondre à la révision de schéma attendue par le CSM.',
    defNbOfTxs: 'GrpHdr/NbOfTxs — nombre de transactions dans ce message.',
    defTtlAmount: 'GrpHdr/TtlIntrBkSttlmAmt — somme des montants interbancaires. Indépendant du montant Tx Inf en mode expert.',
    defTtlCurrency: 'Ccy sur TtlIntrBkSttlmAmt.',
    defGrpSettlementDate: 'GrpHdr/IntrBkSttlmDt — date de règlement au niveau groupe.',
    defSttlmMtd: 'SttlmInf/SttlmMtd — CLRG (compensation), INDA, INGA ou COVE.',
    defInstgAgt: 'BIC de l’agent instructeur (GrpHdr/InstgAgt). Souvent l’agent débiteur.',
    defInstdAgt: 'BIC de l’agent instruit (GrpHdr/InstdAgt). Souvent l’agent créancier.',
    defSvcLvl: 'PmtTpInf/SvcLvl/Cd — niveau de service, typiquement SEPA.',
    defLclInstrm: 'PmtTpInf/LclInstrm/Cd — INST pour SCT Instant. Vide omet l’élément.',
    defChrgBr: 'Porteur des frais : SLEV (selon le service), DEBT, CRED, SHAR.',
    defAccptncDtTm: 'CdtTrfTxInf/AccptncDtTm — acceptation par l’agent débiteur.',
    defStatusMsgId: 'pacs.002 GrpHdr/MsgId — identifiant du rapport de statut lui-même.',
    defOrgnlMsgNmId: 'OrgnlMsgNmId — id versionné complet du pacs.008 d’origine.',
    defAddtlInf: 'StsRsnInf/AddtlInf — complément libre sur un RJCT.',
    titleStructured: 'Construire {{primary}}, obtenir {{pair}}',
    pain: {
      lead: 'Initiation de virement client. Remplissez les champs pour un pain.001 ; le pain.002 apparié montre ACTC ou RJCT avec motif.',
      sectionPmtInf: 'PmtInf',
      sectionStatus: 'pain.002',
      fieldCtrlSum: 'CtrlSum',
      fieldInitgPty: 'Partie initiatrice',
      fieldPmtInfId: 'PmtInfId',
      fieldPmtMtd: 'PmtMtd',
      fieldExecutionDate: 'Date d’exécution demandée',
      fieldStatusMsgId: 'pain.002 MsgId',
      outcome: 'Résultat pain.002',
      outcomeDef: 'Statut groupe / transaction renvoyé par la banque débitrice : accepté (ACTC) ou rejeté (RJCT).',
      outcomeACTC: 'ACTC',
      outcomeRJCT: 'RJCT',
      defMsgId: 'GrpHdr/MsgId — id du pain.001. Référencé comme OrgnlMsgId dans le pain.002.',
      defAmount: 'InstdAmt — montant instruit (décimal avec point).',
      defCtrlSum: 'CtrlSum — somme de contrôle au niveau groupe et PmtInf.',
      defInitgPty: 'InitgPty/Nm — partie ayant initié le fichier (PISP ou entreprise).',
      defPmtInfId: 'PmtInf/PmtInfId — id du bloc d’informations de paiement.',
      defPmtMtd: 'PmtMtd — TRF pour virement.',
      defExecutionDate: 'ReqdExctnDt — date d’exécution souhaitée.',
      defStatusMsgId: 'pain.002 GrpHdr/MsgId.',
      defOrgnlMsgNmId: 'OrgnlMsgNmId — id versionné complet du pain.001 d’origine.',
    },
    camt: {
      lead: 'Demande de rappel (camt.056) et résolution (camt.029). Citez les identifiants du pacs.008 d’origine pour que les deux banques alignent le dossier.',
      sectionCase: 'Dossier',
      sectionBanks: 'Cédant / cessionnaire',
      sectionOriginal: 'Paiement d’origine',
      sectionReason: 'Motif d’annulation',
      sectionAssignment: 'Affectation',
      sectionResolution: 'Résolution camt.029',
      sectionStatus: 'camt.029',
      fieldAssignmentId: 'Id d’affectation',
      fieldCaseId: 'Id de dossier',
      fieldAssignorBic: 'BIC cédant',
      fieldAssigneeBic: 'BIC cessionnaire',
      fieldOrgnlMsgId: 'MsgId d’origine',
      fieldCxlId: 'CxlId',
      fieldCxlReason: 'Motif d’annulation',
      fieldResolutionId: 'Id de résolution',
      fieldCxlStsId: 'CxlStsId',
      outcome: 'Résolution',
      outcomeDef: 'ACCR — rappel accepté ; RJCR — rappel rejeté.',
      outcomeACCR: 'ACCR',
      outcomeRJCR: 'RJCR',
      defAssignmentId: 'Assgnmt/Id — id unique de la demande d’annulation.',
      defCaseId: 'Case/Id — dossier d’investigation cité par les deux banques.',
      defAssignorBic: 'Assgnr — banque émettrice du rappel.',
      defAssigneeBic: 'Assgne — banque destinataire du rappel.',
      defOrgnlMsgId: 'OrgnlMsgId du pacs.008 d’origine.',
      defOrgnlMsgNmId: 'OrgnlMsgNmId — nom versionné du paiement d’origine.',
      defOrgnlAmount: 'OrgnlIntrBkSttlmAmt — montant du virement d’origine.',
      defCxlId: 'CxlId — id de l’instruction d’annulation.',
      defCxlReason: 'CxlRsnInf/Rsn/Cd — DUPL, FRAD, TECH…',
      defAddtlInf: 'Complément libre sur le motif de rappel.',
      defResolutionId: 'camt.029 Assgnmt/Id.',
      defCxlStsId: 'CxlStsId dans le message de résolution.',
    },
    acmt: {
      lead: 'Demande de vérification du bénéficiaire (acmt.023) et rapport (acmt.024). Envoyez le nom tel que saisi par le PSU.',
      sectionParty: 'Partie à vérifier',
      sectionReport: 'Rapport acmt.024',
      sectionStatus: 'acmt.024',
      fieldVrfId: 'Id de vérification',
      fieldReportId: 'MsgId du rapport',
      fieldUpdatedName: 'Nom mis à jour',
      fieldReasonCode: 'Code motif',
      outcome: 'Résultat de correspondance',
      outcomeDef: 'MTCH — correspondance exacte ; NMTC — pas de correspondance / correspondance proche avec nom suggéré.',
      outcomeMTCH: 'MTCH',
      outcomeNMTC: 'NMTC',
      defAssignmentId: 'Assgnmt/MsgId — id de la demande VoP.',
      defVrfId: 'Vrfctn/Id — référence de vérification.',
      defAssignorBic: 'BIC de la banque demandeuse.',
      defAssigneeBic: 'BIC de la banque répondante.',
      defCreditorName: 'Nom saisi par le PSU — ne pas normaliser ici.',
      defCreditorIban: 'IBAN créancier à vérifier.',
      defReportId: 'acmt.024 Assgnmt/MsgId.',
      defUpdatedName: 'UpdtdPtyAndAcctId/Nm — nom légal suggéré en correspondance proche.',
      defReasonCode: 'Rsn/Cd quand Vrfctn est false (ex. CMTC).',
    },
  },
  sample: {
    iso: 'Exemple ISO 20022',
    api: 'Exemple API',
    highlighting: 'Mise en évidence de {{tag}} dans l’arbre',
    usedInFlows: 'Utilisé dans les flux',
    seeInLive: 'Voir en démo grandeur nature',
    openEditor: 'Ouvrir dans l’éditeur d’essai →',
  },
  payload: {
    isoPayload: 'Payload ISO 20022',
    apiPayload: 'Payload API',
    tree: 'arbre',
    raw: 'raw',
    validate: 'valider',
    filterXml: 'Filtrer les balises, ex. DbtrAgt, TxSts…',
    filterJson: 'Filtrer les clés, ex. DbtrAgt, TxSts…',
    filterAria: 'Filtrer le payload',
    notWellFormed: 'Mal formé : {{error}}',
    invalidJson: 'JSON invalide : {{error}}',
    checking: 'Vérification contre le schéma…',
    needXml: 'La validation nécessite du XML. Passez en vue XML ou fournissez une source XML.',
    unreachable: 'Validateur injoignable. Lancez `npm start` pour que Pages Functions serve /api/validate.',
    runAgain: 'Relancer le contrôle',
    passed: 'Contrôles schéma OK.',
    issues: '{{count}} problème(s).',
    detected: 'Détecté {{short}}',
    showInTree: 'Voir dans l’arbre XML',
    elements: '{{count}} éléments',
    depth: 'profondeur {{n}}',
    chars: '{{count}} car.',
    matches: '{{count}} correspondances',
    copy: 'Copier le payload',
    copied: 'Copié',
  },
  standard: {
    publisherDocs: 'Docs éditeur ↗',
    security: 'Profil de sécurité',
    clientAuth: 'Auth client',
    messageSigning: 'Signature de message',
    tokens: 'Jetons',
    certificates: 'Certificats',
    sca: 'Approches SCA',
    gotchas: 'Ce qui piège les intégrateurs',
    apis: 'APIs',
    flows: 'Flux',
    samples: 'Exemples',
    apisEndpoints: 'APIs et endpoints',
    flowsUsing: 'Flux utilisant ce standard',
    samplePayloads: 'Payloads d’exemple',
    steps: '{{count}} étapes',
  },
  live: {
    eyebrow: 'Démo grandeur nature',
    title: 'Les paiements tels qu’ils arrivent',
    lead:
      'Parcourez un panier, un abonnement, un envoi ou une réception P2P, et une boîte banque. Chaque étape montre l’écran consommateur et le payload ISO / API — sans toucher aux pages catalogue.',
    backHub: 'Toutes les scènes',
    explainer: 'Ce qui se passe',
    openFlow: 'Ouvrir le diagramme',
    openPayment: 'Explorateur de paiement',
    openSample: 'Ouvrir la page exemple →',
    openEditor: 'Ouvrir dans l’éditeur d’essai →',
    alexSide: 'Côté Alex (envoi)',
    marieSide: 'Côté Marie (réception)',
    seeBanks: 'Voir ce que font les banques',
    noPayload: 'Pas d’exemple sur ce temps',
    noPayloadBody: 'Cette étape est pure UI ou attend le prochain hop. Avancez pour charger un payload.',
    beat: 'Temps',
    beatsNav: 'Étapes du scénario',
    prev: 'Précédent',
    next: 'Suivant',
    beatN: 'Étape {{n}}',
    statusOk: 'Réglé',
    statusBad: 'Échec',
    statusIdle: 'En cours',
    paid: 'Payé',
    failed: 'Échec',
    noxPlan: 'Nox Standard · 12,99 € / mois',
    scaTitle: 'Authentification forte',
    cta: {
      checkout: 'Payer',
      approve: 'Approuver',
      mandate: 'Signer le mandat',
      send: 'Envoyer',
    },
    method: {
      bank: 'Payer par virement',
      instant: 'Instantané',
      wero: 'Wero',
      card: 'Carte',
    },
    screen: {
      cart: 'Panier',
      method: 'Moyen de paiement',
      sca: 'SCA banque',
      processing: 'Traitement',
      receipt: 'Reçu',
      failed: 'Échec',
      mandate: 'Mandat',
      compose: 'Envoi',
      notify: 'Notification',
      incoming: 'Entrant',
      credited: 'Crédité',
      inbox: 'Boîte',
      consent: 'Consentement',
      statement: 'Relevé',
      hub: 'Hub banque',
    },
    scene: {
      shop: 'Boutique',
      stream: 'Streaming / store',
      wallet: 'Envoyer',
      receive: 'Recevoir',
      bank: 'Banque',
    },
    outcome: {
      happy: 'OK',
      reject: 'Rejet',
      timeout: 'Timeout',
      recall: 'Rappel',
      cancel: 'Annulation',
    },
  },
};
